"use client"

import { useNotificationMutations } from "@/features/notifications/domain/use-notification-mutations"
import { useNotificationsQuery, useUnreadCountQuery } from "@/features/notifications/domain/use-notifications-queries"
import type { TNotification, TNotificationCreate } from "@/types"
import { createContext, useContext, useMemo } from "react"

type CtxValue = {
  notifications: TNotification[]
  unreadCount: number
  addNotification: (data: TNotificationCreate) => void
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  toggleImportant: (id: string) => void
  deleteNotification: (id: string) => void
  updateNotification: (id: string, data: Partial<TNotificationCreate>) => void
}

const NotificationContext = createContext<CtxValue | null>(null)

type TProps = {
  children: Children
}

export function NotificationProvider({ children }: TProps) {
  const { data: notificationsData } = useNotificationsQuery()
  const { data: unreadCountData } = useUnreadCountQuery()
  const { addNotification, markRead, markAllRead, removeNotification, toggleImportant, updateNotification } = useNotificationMutations()

  const notifications: TNotification[] = ((notificationsData as unknown[]) || []).map((entry) => {
    const record = entry as Record<string, unknown>
    return {
      id: String(record.id),
      title: String(record.title ?? ""),
      message: String(record.message ?? ""),
      type: (record.type as TNotification["type"]) ?? "info",
      read: Boolean(record.read),
      important: Boolean(record.important),
      createdAt: String(record.createdAt),
      metadata: record.metadata as Record<string, unknown> | undefined,
    }
  })

  const unreadCount = unreadCountData || 0

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      addNotification: (data: TNotificationCreate) => addNotification({
        ...data,
        message: data.message || "",
        type: data.type || "info"
      }),
      markAsRead: markRead,
      markAllAsRead: () => markAllRead(undefined),
      toggleImportant: (id: string) => toggleImportant(id),
      deleteNotification: removeNotification,
      updateNotification: (id: string, data: Partial<TNotificationCreate>) => updateNotification({ id, data })
    }),
    [notifications, unreadCount, addNotification, markRead, markAllRead, toggleImportant, removeNotification, updateNotification],
  )

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

export function useNotifications(): CtxValue {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider")
  }
  return context
}
